import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import type { ContentTopic } from '../../types';
import { ContentPlanApiClient } from '../../services/ContentPlanApiClient';
import { useTopicResearchRuns } from '../../hooks/useTopicResearchRuns';
import { ResearchStudyDialog } from './ResearchStudyDialog';

function formatTime(ts: any): string {
  if (!ts) return '-';
  if (typeof ts === 'string') return new Date(ts).toLocaleString();
  if (ts.toDate) return (ts.toDate() as Date).toLocaleString();
  if (ts.seconds) return new Date(ts.seconds * 1000).toLocaleString();
  return String(ts);
}

function phaseProgress(run: any): string {
  const phases = ['brief', 'research', 'analysis'] as const;
  const done = phases.filter((phase) => run?.phases?.[phase]?.status === 'completed').length;
  return `${done}/3`;
}

function topFacts(run: any): string[] {
  const facts = run?.analysis?.keyFacts;
  if (Array.isArray(facts) && facts.length > 0) {
    return facts
      .map((fact: any) => String(fact.claim ?? fact.fact ?? '').trim())
      .filter(Boolean)
      .slice(0, 2);
  }
  if (run?.brief?.refinedDescription) return [String(run.brief.refinedDescription).slice(0, 180)];
  if (run?.research?.aiSummary) return [String(run.research.aiSummary).slice(0, 180)];
  return ['No completed facts yet'];
}

function statusColor(status: string): 'default' | 'primary' | 'success' | 'warning' | 'error' {
  if (status === 'completed') return 'success';
  if (status === 'failed') return 'error';
  if (status === 'cancelled') return 'warning';
  if (status?.includes('running') || status === 'analysing') return 'primary';
  return 'default';
}

export interface ResearchStudiesTabProps {
  projectId: string;
  userId: string;
  authorId: string;
  topic: ContentTopic;
}

export const ResearchStudiesTab: React.FC<ResearchStudiesTabProps> = ({
  projectId,
  userId,
  authorId,
  topic,
}) => {
  const { runs, loading, error: loadError } = useTopicResearchRuns(projectId, topic.id, authorId);
  const client = useMemo(() => new ContentPlanApiClient(), []);
  const [dialogRunId, setDialogRunId] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeBusy, setActiveBusy] = useState<string | null>(null);
  const [deleteBusy, setDeleteBusy] = useState<string | null>(null);

  const selectedRun = dialogRunId ? runs.find((run: any) => run.id === dialogRunId) ?? null : null;

  const openNew = () => {
    setDialogRunId(null);
    setDialogOpen(true);
  };

  const openRun = (runId: string) => {
    setDialogRunId(runId);
    setDialogOpen(true);
  };

  const setActive = async (run: any, active: boolean) => {
    setError(null);
    setActiveBusy(run.id);
    try {
      const resp = await client.setResearchRunActive({
        projectId,
        authorId,
        runId: run.id,
        active,
      });
      if (!resp.success) throw new Error(resp.error ?? 'active update failed');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setActiveBusy(null);
    }
  };

  const deleteRun = async (runId: string) => {
    setError(null);
    setDeleteBusy(runId);
    try {
      const resp = await client.deleteResearchRun({ projectId, authorId, runId });
      if (!resp.success) throw new Error(resp.error ?? 'delete failed');
      if (dialogRunId === runId) {
        setDialogOpen(false);
        setDialogRunId(null);
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setDeleteBusy(null);
    }
  };

  return (
    <Stack spacing={2}>
      {(error || loadError) && (
        <Alert severity="error">{error ?? loadError?.message}</Alert>
      )}

      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="subtitle1" sx={{ flex: 1 }}>
          Research history
        </Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={openNew}
          data-testid="research-add-study"
        >
          Add research
        </Button>
      </Stack>

      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'auto' }}>
        <Table size="small" data-testid="research-studies-table">
          <TableHead>
            <TableRow>
              <TableCell>Study</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Active</TableCell>
              <TableCell>Model</TableCell>
              <TableCell>Facts</TableCell>
              <TableCell>Updated</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && runs.length === 0 && (
              <TableRow>
                <TableCell colSpan={7}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={14} />
                    <Typography variant="caption" color="text.secondary">Loading research studies...</Typography>
                  </Stack>
                </TableCell>
              </TableRow>
            )}
            {!loading && runs.length === 0 && (
              <TableRow>
                <TableCell colSpan={7}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    No research studies yet. Add one to run the 3-phase research workflow.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {runs.map((run: any) => {
              const eligible = run.status === 'completed' && Boolean(run.analysis);
              const facts = topFacts(run);
              return (
                <TableRow
                  key={run.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => openRun(run.id)}
                  data-testid={`research-study-row-${run.id}`}
                >
                  <TableCell sx={{ minWidth: 240 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {run.title ?? run.focus?.label ?? run.brief?.refinedDescription ?? 'Research study'}
                    </Typography>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                      {run.focus?.kind && <Chip size="small" variant="outlined" label={run.focus.kind} />}
                      {run.focus?.aspectKey && <Chip size="small" variant="outlined" label={run.focus.aspectKey} />}
                      <Chip size="small" variant="outlined" label={`phases ${phaseProgress(run)}`} />
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" color={statusColor(run.status)} label={run.status ?? 'unknown'} />
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Tooltip title={eligible ? 'Use as default research grounding for posts' : 'Complete Phase 3 before activating'}>
                      <span>
                        <Switch
                          size="small"
                          checked={Boolean(run.isActiveForPosts)}
                          disabled={!eligible || activeBusy === run.id}
                          onChange={(e) => setActive(run, e.target.checked)}
                          data-testid={`research-study-active-${run.id}`}
                        />
                      </span>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ minWidth: 160 }}>
                    <Typography variant="caption" color="text.secondary">
                      {[
                        run.phases?.research?.provider,
                        run.phases?.research?.model,
                        run.phases?.research?.researchDepth,
                      ].filter(Boolean).join(' / ') || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ minWidth: 300 }}>
                    <Stack spacing={0.5}>
                      {facts.map((fact, idx) => (
                        <Typography key={idx} variant="caption" color={idx === 0 ? 'text.primary' : 'text.secondary'}>
                          {fact}
                        </Typography>
                      ))}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {formatTime(run.updatedAt ?? run.createdAt)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                    <Tooltip title="Open study">
                      <IconButton size="small" onClick={() => openRun(run.id)}>
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete study">
                      <IconButton
                        size="small"
                        color="default"
                        disabled={deleteBusy === run.id}
                        onClick={() => deleteRun(run.id)}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>

      <ResearchStudyDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        projectId={projectId}
        userId={userId}
        authorId={authorId}
        topic={topic}
        run={selectedRun}
        onRunCreated={(runId) => {
          setDialogRunId(runId);
          setDialogOpen(true);
        }}
      />
    </Stack>
  );
};

export default ResearchStudiesTab;
